import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, TruckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import weighingService from '../../services/weighing';
import { Transporteur } from '../../types';
import { AddCarrierModal, EditCarrierModal } from '../modals';

const CarrierManagement: React.FC = () => {
  const [carriers, setCarriers] = useState<Transporteur[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState<Transporteur | null>(null);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Non renseigné';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  
  // États des modales
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const loadCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ordering: 'nom', // Tri alphabétique par nom
        search: searchTerm || undefined,
        _t: Date.now() // Cache-busting
      };
      
      const response = await weighingService.getTransporteurs(params);
      if (response && response.results) {
        setCarriers(response.results || []);
        setTotalCount(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / pageSize));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des transporteurs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  const handleAddCarrier = async (carrierData: any) => {
    try {
      await weighingService.createTransporteur(carrierData);
      await loadCarriers();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du transporteur:', error);
      alert('Erreur lors de l\'ajout du transporteur');
    }
  };

  const handleEditCarrier = async (carrierData: Partial<any>) => {
    if (!selectedCarrier) return;
    
    try {
      await weighingService.updateTransporteur(selectedCarrier.id, carrierData);
      await loadCarriers();
      setIsEditModalOpen(false);
      setSelectedCarrier(null);
      // Afficher un message de succès
      alert('Transporteur modifié avec succès !');
    } catch (error) {
      console.error('Erreur lors de la modification du transporteur:', error);
      alert('Erreur lors de la modification du transporteur');
    }
  };

  const handleDeleteCarrier = async () => {
    if (!selectedCarrier) return;
    
    try {
      await weighingService.deleteTransporteur(selectedCarrier.id);
      await loadCarriers();
      setIsDeleteModalOpen(false);
      setSelectedCarrier(null);
      // Afficher un message de succès
    } catch (error) {
      console.error('Erreur lors de la suppression du transporteur:', error);
      alert('Erreur lors de la suppression du transporteur');
    }
  };

  // Fonctions de pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset à la première page lors de la recherche
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec recherche et bouton d'ajout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher un transporteur..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Ajouter un transporteur
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <div className="p-2 bg-blue-400 rounded-lg">
              <TruckIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">Total Transporteurs</p>
              <p className="text-2xl font-bold">{carriers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <div className="p-2 bg-green-400 rounded-lg">
              <TruckIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Actifs</p>
              <p className="text-2xl font-bold">{carriers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <div className="p-2 bg-purple-400 rounded-lg">
              <TruckIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Zones</p>
              <p className="text-2xl font-bold">{new Set(carriers.map(c => c.zone)).size}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table des transporteurs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {carriers && carriers.length > 0 ? carriers.map((carrier) => (
                <tr key={carrier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {carrier.nom}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{carrier.statut_display || carrier.statut}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{carrier.zone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{carrier.telephone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{carrier.email || 'Non renseigné'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedCarrier(carrier);
                          setIsDetailsModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Voir les détails"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCarrier(carrier);
                          setIsEditModalOpen(true);
                        }}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded"
                        title="Modifier"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCarrier(carrier);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <TruckIcon className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun transporteur</h3>
                      <p className="text-sm text-gray-500">
                        {searchTerm ? 'Aucun transporteur ne correspond à votre recherche.' : 'Commencez par ajouter votre premier transporteur.'}
                      </p>
                      {!searchTerm && (
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          <PlusIcon className="h-5 w-5 mr-2" />
                          Ajouter un transporteur
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Contrôles de pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> à <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span> sur <span className="font-medium">{totalCount}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Numéros de page */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      <AddCarrierModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddCarrier}
      />

             {/* Modales pour l'édition, suppression et détails */}
       {selectedCarrier && (
         <>
                       <EditCarrierModal
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setSelectedCarrier(null);
              }}
              onSave={handleEditCarrier}
              carrier={selectedCarrier}
            />

           {isDeleteModalOpen && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
               <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
                 <h3 className="text-lg font-medium mb-4">Supprimer le transporteur</h3>
                 <p className="text-gray-600 mb-4">
                   Êtes-vous sûr de vouloir supprimer "{selectedCarrier.nom}" ?
                 </p>
                 <div className="flex justify-end space-x-3">
                   <button
                     onClick={() => {
                       setIsDeleteModalOpen(false);
                       setSelectedCarrier(null);
                     }}
                     className="px-4 py-2 text-gray-600 hover:text-gray-800"
                   >
                     Annuler
                   </button>
                   <button
                     onClick={handleDeleteCarrier}
                     className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                   >
                     Supprimer
                   </button>
                 </div>
               </div>
             </div>
           )}

           {isDetailsModalOpen && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
               <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
                 <h3 className="text-lg font-medium mb-4">Détails du transporteur</h3>
                 <div className="space-y-3">
                   <div>
                     <span className="font-medium">Nom:</span> {selectedCarrier.nom}
                   </div>
                   <div>
                     <span className="font-medium">Adresse:</span> {selectedCarrier.adresse}
                   </div>
                   <div>
                     <span className="font-medium">Téléphone:</span> {selectedCarrier.telephone}
                   </div>
                   <div>
                     <span className="font-medium">Email:</span> {selectedCarrier.email || 'Non renseigné'}
                   </div>
                   <div>
                     <span className="font-medium">Zone:</span> {selectedCarrier.zone}
                   </div>
                   <div>
                     <span className="font-medium">Statut:</span> {selectedCarrier.statut_display || selectedCarrier.statut}
                   </div>
                  <div>
                    <span className="font-medium">Créé le:</span> {formatDate(selectedCarrier.created_at)}
                  </div>
                  <div>
                    <span className="font-medium">Modifié le:</span> {formatDate(selectedCarrier.updated_at)}
                  </div>
                 </div>
                 <div className="flex justify-end mt-6">
                   <button
                     onClick={() => {
                       setIsDetailsModalOpen(false);
                       setSelectedCarrier(null);
                     }}
                     className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                   >
                     Fermer
                   </button>
                 </div>
               </div>
             </div>
           )}
         </>
       )}
    </div>
  );
};

export default CarrierManagement;
